// src/components/Finanzas/RelacionDetalleContenido.jsx
// Bloque compartido de visualizacion de una RelacionGastos cerrada o en
// borrador. Se reutiliza desde:
//   - MiHistorialBolsillo.jsx (body del accordion)
//   - MisRelacionesGastos.jsx (body del accordion)
//   - DetalleRelacion.jsx (vista de detalle read-only completo)
//
// Centraliza la presentacion para evitar drift entre las pantallas. RevisionCierre.jsx
// mantiene su layout propio porque incluye preview de comprobantes por mes
// antes de aprobar, que es exclusivo del flujo BORRADOR -> APROBADA.
//
// Props:
//   - relacion:   documento RelacionGastos completo (con saldos, totales, ids).
//   - gastos:     array de GastoContable (ya hidratado por el padre).
//   - entregas:   array de EntregaDinero (ya hidratado).
//   - traslados:  array de TrasladoDinero (ya hidratado).
//   - bolsilloId: opcional; sino se infiere de relacion.bolsillo._id.
//   - cargandoDetalle: boolean para mostrar spinner mientras el padre hidrata.
//   - errorDetalle: string opcional con error del fetch del padre.
//   - onReintentar: callback opcional para boton "Reintentar".

import React from 'react';
import { Row, Col, Table, Badge, Alert, Spinner, Button } from 'react-bootstrap';
import { FaFileInvoiceDollar, FaExchangeAlt } from 'react-icons/fa';
import TablaMovimientosCierre from './TablaMovimientosCierre';
import { formatoFechaColombia } from '../../utils/fechasColombia';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const RelacionDetalleContenido = ({
  relacion,
  gastos = [],
  entregas = [],
  traslados = [],
  bolsilloId,
  cargandoDetalle = false,
  errorDetalle = null,
  onReintentar = null,
  onVerObservaciones = null
}) => {
  if (!relacion) return null;

  const idBolsillo = bolsilloId || relacion.bolsillo?._id || relacion.bolsillo;

  if (cargandoDetalle) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" /> Cargando detalle…
      </div>
    );
  }

  if (errorDetalle) {
    return (
      <Alert variant="danger" className="py-2 small">
        {errorDetalle}
        {onReintentar && (
          <Button size="sm" variant="link" className="p-0 ms-2" onClick={onReintentar}>
            Reintentar
          </Button>
        )}
      </Alert>
    );
  }

  const totalRecibido =
    (relacion.montoTotalEntregasRecibidas || 0) +
    (relacion.montoTotalTrasladosRecibidos || 0);

  return (
    <>
      {/* Resumen de saldos */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <small className="text-muted d-block">Saldo inicial</small>
          <strong>{formatoCOP(relacion.saldoInicial)}</strong>
        </Col>
        <Col md={3}>
          <small className="text-muted d-block">Total gastos</small>
          <strong className="text-primary">{formatoCOP(relacion.montoTotalGastos)}</strong>
        </Col>
        <Col md={3}>
          <small className="text-muted d-block">Total recibido</small>
          <strong className="text-success">{formatoCOP(totalRecibido)}</strong>
        </Col>
        <Col md={3}>
          <small className="text-muted d-block">Saldo final</small>
          <strong className={relacion.saldoFinal >= 0 ? 'text-success' : 'text-danger'}>
            {formatoCOP(relacion.saldoFinal)}
          </strong>
        </Col>
      </Row>

      {relacion.observaciones && (
        <Alert variant="info" className="py-2 mb-3 small">
          <strong>Observaciones:</strong> {relacion.observaciones}
        </Alert>
      )}

      {/* Periodo */}
      <p className="small text-muted mb-3">
        <strong>Periodo:</strong> {formatoFechaColombia(relacion.fechaInicio)} – {formatoFechaColombia(relacion.fechaCierre)}
        {relacion.mesesRepresentados?.length > 0 && (
          <> · <strong>Meses:</strong> {relacion.mesesRepresentados.join(', ')}</>
        )}
      </p>

      {/* Gastos */}
      <h6 className="mt-3 mb-2">
        <FaFileInvoiceDollar className="me-1 text-primary" />
        Gastos ({gastos.length})
      </h6>
      {gastos.length === 0 ? (
        <p className="text-muted small mb-3">Sin gastos en este cierre.</p>
      ) : (
        <Table hover size="sm" className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Fecha</th>
              <th>Descripción</th>
              <th>Categoría</th>
              <th>Tercero</th>
              <th>Factura</th>
              <th>OT</th>
              <th className="text-end">Valor</th>
              <th className="text-end">IVA</th>
            </tr>
          </thead>
          <tbody>
            {gastos.map((g) => (
              <tr key={g._id}>
                <td>{formatoFechaColombia(g.fecha)}</td>
                <td><small>{g.descripcion}</small></td>
                <td><small className="text-muted">{g.categoria || '—'}</small></td>
                <td>
                  <small>
                    {g.proveedor?.nombre || g.proveedorSinCrear?.nombre || '—'}
                  </small>
                </td>
                <td>
                  {g.tieneFactura
                    ? <Badge bg="primary">{g.numeroFactura || 'sí'}</Badge>
                    : <small className="text-muted">—</small>}
                </td>
                <td><small className="text-muted">{g.ordenTrabajo?.numeroOT || '—'}</small></td>
                <td className="text-end fw-bold">{formatoCOP(g.valorTotal)}</td>
                <td className="text-end">{formatoCOP(g.iva || 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Movimientos de dinero (entregas + traslados) */}
      <h6 className="mt-4 mb-2">
        <FaExchangeAlt className="me-1 text-info" />
        Movimientos de dinero ({entregas.length + traslados.length})
      </h6>
      <TablaMovimientosCierre
        entregas={entregas}
        traslados={traslados}
        bolsilloId={idBolsillo}
        onVerObservaciones={onVerObservaciones}
      />
    </>
  );
};

export default RelacionDetalleContenido;
